import React, { useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  IconButton,
  Link,
  Text,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";

interface FAQ {
  question: string;
  answer: string | string[];
  top10Order?: number;
}

interface FAQGroup {
  groupName: string;
  faqs: FAQ[];
}

const faqData: FAQGroup[] = [
  {
    groupName: "Exploring the Push Notifications Page",
    faqs: [
      {
        question: "What does the Push Notifications page show?",
        answer:
          "All notification schedules for your business, with filters for Name, Status, and Sort Order.",
        top10Order: 10,
      },
      {
        question: "How do I see a schedule’s details?",
        answer:
          "Select a schedule from the list to view its name, message, status, send time, and available actions.",
      },
      {
        question: "What actions can I take on a schedule?",
        answer: [
          "Depending on status:",
          "• Edit (Draft only)",
          "• Revert to Draft (Scheduled, >1 hr before send)",
          "• Clone (All statuses)",
          "• Delete (Draft only)",
        ],
      },
      {
        question: "What do the statuses mean?",
        answer: [
          "Draft – Editable, not scheduled",
          "Scheduled – Locked and queued",
          "Sent – Successfully delivered",
        ],
      },
      {
        question: "Can I edit a Scheduled notification?",
        answer: [
          "Yes, but only more than 1 hour before the send time by reverting to Draft.",
        ],
        top10Order: 8,
      },
      {
        question: "Can I clone an existing notification?",
        answer: [
          "Yes. Use the Clone option to duplicate a previous notification and adjust details without starting from scratch.",
        ],
        top10Order: 9,
      },
      {
        question: "What do the top-right buttons do?",
        answer: [
          "Cancel – Exit without saving",
          "Save Draft – Save but don’t schedule",
          "Schedule – Save and queue for sending",
        ],
      },
      {
        question: "What does the Geo-fence badge mean?",
        answer: [
          "The Geo-fence badge indicates that the campaign contains one or more Geo-fence location message overrides.",
          "",
          "Campaigns without any overridden location messages will not display the badge.",
        ],
      },
    ],
  },
  {
    groupName: "Creating & Scheduling Notifications",
    faqs: [
      {
        question: "What types of notifications can I send?",
        answer:
          "Currently, Cherryhub supports General Notifications (message-based). More notification types such as awarding points may be added in the future.",
      },
      {
        question: "Can I create a campaign with only Geo-fence Notifications?",
        answer:
          "Yes. The Push Notification message is optional. You can create a campaign using only Geo-fence Notifications, provided at least one Geo-fence location has a configured campaign message.",
      },
      {
        question: "Can I create a campaign with only a Push Notification?",
        answer:
          "Yes. Geo-fence Notifications are optional. You can create a standard Push Notification without configuring any Geo-fence locations.",
      },
      {
        question:
          "Do I need both a Push Notification and Geo-fence Notification to create a campaign?",
        answer: [
          "No. Push Notifications and Geo-fence Notifications are configured independently.",
          "",
          "You can create a campaign with:",
          "• A Push Notification only",
          "• Geo-fence Notifications only",
          "• Both together",
          "",
          "However, at least one notification message must be configured before the campaign can be saved.",
        ],
      },
      {
        question: "What type of messages am I allowed to send?",
        answer: [
          "Messages must be directly related to the member’s card, membership, or relevant benefits.",
          "Examples of allowed messages:",
          '• "Present your membership card tonight for exclusive drink specials."',
          '• "Your membership benefits are available this weekend!"',
          "Messages cannot be general spam, such as promoting events without referencing the membership or loyalty program.",
        ],
        top10Order: 1,
      },
      {
        question:
          "How many characters can my push notification message contain?",
        answer:
          "You can enter up to 150 characters, including emojis. Line breaks (carriage returns) are not supported.",
        top10Order: 4,
      },
      {
        question: "Why does the preview show the Cherryhub icon?",
        answer:
          "The preview uses a placeholder icon. When delivered to users, the notification will show your actual pass logo. This depends on the design associated with the member’s digital pass.",
      },
      {
        question:
          "What is the difference between a default location message and a campaign message?",
        answer: [
          "A default location message is configured as part of your pass configuration and is used whenever no campaign-specific message has been provided.",
          "",
          "A campaign message temporarily overrides the default message for the duration of the campaign, allowing you to tailor messaging for specific promotions or events.",
        ],
      },
      {
        question: "What happens when I override a location message?",
        answer:
          "When you enter a campaign message for a location, it temporarily replaces that location’s default message while the campaign is active. Once the campaign ends, the location automatically reverts to its default message.",
      },
      {
        question: "Why is the End Date required for Geo-fence campaigns?",
        answer: [
          "The End Date only becomes required when one or more Geo-fence location messages have been overridden.",
          "",
          "The End Date determines when the campaign messages stop and the default location messages are automatically restored. If no location messages have been overridden, the End Date remains hidden.",
        ],
      },
      {
        question: "When can I schedule a push notification?",
        answer:
          "Set the timezone, send date/time (min 1 hr ahead), and stop condition (Deliver Anytime or Stop After Time). Notifications can be scheduled for one hour from now or later. This ensures Cherryhub has time to queue and process the message.",
        top10Order: 5,
      },
      {
        question: 'What does the "Stop Sending After" option do?',
        answer: [
          "This option stops Cherryhub from continuing to push the notification after a certain time.",
          "This is useful for time-sensitive promotions such as:",
          '• "Event starts at 7PM tonight"',
          "You can set it to stop sending after the event begins.\n",
          "Note: If a member’s device is offline (e.g., airplane mode), the device may still show the notification late once it reconnects.",
        ],
        top10Order: 6,
      },
      {
        question:
          "What happens if I save a notification without scheduling it?",
        answer: [
          "Saving as a Draft lets you:",
          "• Store it inside your list",
          "• Reopen and edit it anytime",
          "• Clone it later",
          "• Delete it if no longer needed",
          "Nothing is sent until you schedule and confirm the notification.",
        ],
      },
    ],
  },
  {
    groupName: "AI Message Quality & Compliance",
    faqs: [
      {
        question:
          "What do the AI message risk rating and spam indicator mean when creating a notification?",
        answer: [
          "When creating a push notification, the system provides AI-based guidance to help improve message quality and deliverability.",
          "",
          "AI Message Risk Rating",
          "• Safe → Message is ready to send",
          "• Review Required → Message may need edits before sending, but it can still be sent. You will see a warning before confirming send",
          "• High Risk → Message is likely to be filtered or rejected and cannot be sent until it is edited and the risk level is reduced",
          "",
          "This rating helps identify messages that may need tone, clarity, or compliance adjustments. AI suggestions are helpful but may not always be 100% accurate.",
          "",
          "Spam Indicator",
          "The spam indicator highlights when a notification may be considered over-messaged or high frequency for a member, based on recent notification activity.",
          "",
          "It is used to help prevent over-sending and protect engagement quality.",
          "",
          "The indicator may appear when:",
          "• Notifications have been sent above a defined frequency threshold",
          "• Members have received multiple notifications in a short period",
          "• Messaging volume is high within a recent time window",
          "",
          "If flagged, it is recommended to:",
          "• Review recent send activity",
          "• Space out notifications where possible",
          "• Avoid sending additional messages in a short timeframe",
          "",
          "This helps ensure members are not over-notified and improves overall engagement performance.",
        ],
      },
    ],
  },
  {
    groupName: "Understanding Your Audience",
    faqs: [
      {
        question: "Who receives my notification?",
        answer:
          "Notifications are delivered to members who match your selected audience. For Geo-fence Notifications, members must also enter one of the configured Geo-fence locations before the notification is triggered.",
        top10Order: 2,
      },
      {
        question: "How can members opt-out of receiving push notifications?",
        answer:
          'Members must manually disable push notifications on the rear of the pass if they choose. Please note that this won\'t change the "Eligible members" count in the portal currently but may do in the future.',
        top10Order: 3,
      },
      {
        question: "Can I see how many members the notification will reach?",
        answer: [
          "Yes. Before sending, Cherryhub shows the total number of eligible members, based on:",
          "• Who has downloaded a pass",
          "• Any filters you applied (e.g., financial only)",
          "You can also view and export the full list.",
        ],
      },
      {
        question: "Can I save my audience for future campaigns?",
        answer: [
          "Yes. You can create and save your selected member filters as a Saved Segment.",
          "",
          "Saved Segments allow you to quickly reuse the same audience across future notification campaigns without rebuilding the filters each time.",
        ],
      },
      {
        question:
          "What’s the difference between Manual Filters and Saved Segments?",
        answer: [
          "• Manual Filters apply only to the current campaign and are not saved.",
          "• Saved Segments store your selected filters so they can be reused across future campaigns.",
          "",
          "Only one audience type can be used at a time. Selecting a Saved Segment clears any Manual Filters, and applying Manual Filters removes the selected Saved Segment.",
        ],
      },
    ],
  },
  {
    groupName: "Delivery Behaviour",
    faqs: [
      {
        question: "How many push notifications can I send in a day?",
        answer:
          "Google allows up to 3 notifications per device per day. Anything beyond that may be blocked by the device. " +
          "Apple has no strict published limit but also discourages excessive messaging.",
        top10Order: 7,
      },
      {
        question: "How quickly do notifications send?",
        answer:
          "Cherryhub sends messages continuously through the entire eligible list. If your list is large, it does not send in batches. It moves through the list as fast as possible.",
      },
      {
        question: "When are Geo-fence Notifications delivered?",
        answer: [
          "Geo-fence Notifications are delivered when an eligible member enters one of your configured Geo-fence locations.",
          "",
          "The notification uses either the location’s default message or a campaign-specific override if one has been configured.",
        ],
      },
      {
        question: "How often will members receive a Geo-fence Notification?",
        answer: [
          "Geo-fence Notifications are triggered when eligible members enter a configured venue location.",
          "",
          "Members will see either:",
          "• The default location message, or",
          "• The campaign message, if a campaign message is active for that location",
          "",
          "If a member visits the venue every day, they may receive the same Geo-fence Notification each day, depending on their device settings and whether the location trigger is detected.",
        ],
      },
      {
        question:
          "Will members receive the same Geo-fence Notification every time they enter a location?",
        answer: [
          "Yes. Members may receive the same Geo-fence Notification each time they enter the configured location.",
          "",
          "The message shown will depend on whether a campaign message is active at that time:",
          "• If no campaign message is active, the member will receive the default location message.",
          "• If a campaign message is active, the member will receive the campaign message.",
          "",
          "When the campaign ends, the message will revert back to the default location message.",
        ],
      },
    ],
  },
  {
    groupName: "Cost",
    faqs: [
      {
        question: "Will I be charged per push notification?",
        answer: [
          "Currently, there is no per-message cost and no contract limit on the number of push notifications.",
          "This may change in the future, but for now there are:",
          "• No extra fees for sending notifications",
          "• No penalties for undelivered messages",
        ],
      },
      {
        question: "Are Geo-fence Notifications included in the same pricing?",
        answer: [
          "Yes. Geo-fence Notifications are included as part of the Push Notifications feature.",
          "",
          "If your organisation has access to Push Notifications, you also have access to Geo-fence Notifications at no additional cost. Geo-fence Notifications can be configured within the same notification campaign and do not incur any additional charges.",
        ],
      },
    ],
  },
];

interface FAQSectionProps {
  isFullView?: boolean;
  onViewAllClick?: () => void;
  onBackClick?: () => void;
}

export const FAQSection = ({
  isFullView = false,
  onViewAllClick,
  onBackClick,
}: FAQSectionProps) => {
  const [expandedByGroup, setExpandedByGroup] = useState<
    Record<number, number | number[]>
  >({});
  const [expandedIndex, setExpandedIndex] = useState<number | number[]>(-1);

  const renderAnswer = (answer: string | string[]) => {
    if (Array.isArray(answer)) {
      return (
        <Box>
          {answer.map((line, index) => {
            // Check if it's a bullet point
            const isBullet = line.startsWith("•");

            return (
              <Text key={index} mb={isBullet ? 1 : 2} pl={isBullet ? 4 : 0}>
                {line}
                <br />
              </Text>
            );
          })}
        </Box>
      );
    }
    return <Text>{answer}</Text>;
  };

  // Get top 10 FAQs sorted by top10Order
  const getTop10FAQs = () => {
    const allFAQs: FAQ[] = [];
    faqData.forEach((group) => {
      group.faqs.forEach((faq) => {
        if (faq.top10Order !== undefined) {
          allFAQs.push(faq);
        }
      });
    });
    return allFAQs.sort((a, b) => (a.top10Order || 0) - (b.top10Order || 0));
  };

  return (
    <Box mt={isFullView ? 0 : 6}>
      <Flex alignItems="center" justifyContent="space-between" mb={4}>
        <Flex alignItems="center" gap={2}>
          {isFullView && onBackClick && (
            <IconButton
              aria-label="Go back"
              icon={<ArrowBackIcon />}
              variant="ghost"
              size="sm"
              onClick={onBackClick}
            />
          )}
          <Text fontSize="md" fontWeight="bold">
            FAQs
          </Text>
        </Flex>
        {!isFullView && onViewAllClick && (
          <Link
            color="red.500"
            fontSize="sm"
            fontWeight="medium"
            onClick={onViewAllClick}
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
          >
            View all →
          </Link>
        )}
      </Flex>

      {!isFullView ? (
        <Accordion
          allowToggle
          index={expandedIndex}
          onChange={(idx) => setExpandedIndex(idx)}
        >
          {getTop10FAQs().map((faq, faqIndex) => (
            <AccordionItem
              key={faqIndex}
              borderBottom="1px solid"
              borderBottomColor="gray.200"
              mb={2}
              borderTopWidth={0}
            >
              {({ isExpanded }) => (
                <>
                  <AccordionButton
                    bg={isExpanded ? "gray.50" : "white"}
                    _hover={{ bg: "gray.50" }}
                    borderRadius="md"
                    py={3}
                    px={4}
                  >
                    <Box flex="1" textAlign="left">
                      <Text fontSize="sm" fontWeight="medium">
                        {faq.question}
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} px={4}>
                    <Text fontSize="sm" color="gray.700">
                      {renderAnswer(faq.answer)}
                    </Text>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      ) : (
        faqData.map((group, groupIndex) => (
          <Box key={groupIndex} mb={6}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
              {group.groupName}
            </Text>
            <Accordion
              allowToggle
              index={expandedByGroup[groupIndex] ?? -1}
              onChange={(idx) =>
                setExpandedByGroup((prev) => ({ ...prev, [groupIndex]: idx }))
              }
            >
              {group.faqs.map((faq, faqIndex) => (
                <AccordionItem
                  key={faqIndex}
                  borderBottom="1px solid"
                  borderBottomColor="gray.200"
                  mb={2}
                  borderTopWidth={0}
                >
                  {({ isExpanded }) => (
                    <>
                      <AccordionButton
                        bg={isExpanded ? "gray.50" : "white"}
                        _hover={{ bg: "gray.50" }}
                        borderRadius="md"
                        py={3}
                        px={4}
                      >
                        <Box flex="1" textAlign="left">
                          <Text fontSize="sm" fontWeight="medium">
                            {faq.question}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4} px={4}>
                        <Text fontSize="sm" color="gray.700">
                          {renderAnswer(faq.answer)}
                        </Text>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              ))}
            </Accordion>
          </Box>
        ))
      )}
    </Box>
  );
};
